import { type FullName_name$key } from '#app/__generated__/relay/FullName_name.graphql.js';
import { Button } from '#app/components/Buttons/Button.js';
import { FormField } from '#app/components/FormField/FormField.js';
import { InlineEditContainer } from '#app/components/InlineEditContainer/InlineEditContainer.js';
import { Link } from '#app/components/Link/index.js';
import { Text } from '#app/components/Primitives/Text.js';
import { ProBadge } from '#app/components/ProBadge/ProBadge.js';
import { TextField } from '#app/components/TextField/TextField.js';
import { useToast } from '#app/components/Toasts/hooks/useToast.js';
import { useEditSidebar } from '#app/features/profile/hooks/useEditSidebar.js';
import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { useForm } from '#app/hooks/useForm.js';
import { useTrackInput } from '#app/hooks/useTrackInput.js';
import {
  EditableActions,
  ValidationRow,
  ValidationText,
} from './InlineEditing.styles.js';
import { EditIcon } from '@contra/icons/icons';
import { graphql, useFragment } from '@contra/relay';
import { getRoute } from '@contra/routes';
import { captureException } from '@contra/sentry/react';
import { ErrorMessage } from '@hookform/error-message';
import { useCallback, useState } from 'react';
import { css, styled } from 'styled-components';
import { z } from 'zod';

const InputGroup = styled.div`
  margin-top: 16px;
  display: flex;
  align-items: center;

  > * + * {
    margin-left: 12px;
  }
`;

const FullNameContainer = styled(Text)`
  ${({ theme }) => css`
    text-align: center;
    padding: 0 0 0 24px;
    word-break: break-word;

    ${theme.mediaQueries.lg} {
      padding: 8px 0;
    }
  `}
`;

const ProBadgeInlineContainer = styled.div`
  display: inline-block;
  vertical-align: middle;
`;

type FullNameProps = {
  readonly disableEditing?: boolean;
  readonly openLinkInNewWindow?: boolean;
  readonly userProfileRef: FullName_name$key;
};

type ReadonlyFullNameProps = {
  readonly displayUsername: string;
  readonly firstName: string;
  readonly hideLink?: boolean;
  readonly lastName: string;
  readonly openLinkInNewWindow?: boolean;
  readonly showProBadge: boolean;
};

const FullNameText = (props: {
  readonly firstName: string;
  readonly lastName: string;
  readonly showProBadge: boolean;
}) => {
  return (
    <FullNameContainer
      as="h1"
      textStyle="fourthLevelHeadline"
    >
      {props.firstName} {props.lastName}{' '}
      {props.showProBadge ? (
        <ProBadgeInlineContainer>
          <ProBadge size="md" />
        </ProBadgeInlineContainer>
      ) : null}
    </FullNameContainer>
  );
};

const ReadonlyFullName = ({
  displayUsername,
  firstName,
  hideLink = false,
  lastName,
  openLinkInNewWindow,
  showProBadge,
}: ReadonlyFullNameProps) => {
  return hideLink ? (
    <FullNameText
      firstName={firstName}
      lastName={lastName}
      showProBadge={showProBadge}
    />
  ) : (
    <Link
      to={
        displayUsername
          ? getRoute('/@username', { args: { username: displayUsername } })
          : getRoute('/')
      }
      {...(openLinkInNewWindow ? { target: '_blank' } : {})}
    >
      <FullNameText
        firstName={firstName}
        lastName={lastName}
        showProBadge={showProBadge}
      />
    </Link>
  );
};

export const FullName = ({
  disableEditing = false,
  openLinkInNewWindow,
  userProfileRef,
}: FullNameProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const toast = useToast();

  const data = useFragment<FullName_name$key>(
    graphql`
      fragment FullName_name on UserProfile {
        firstName
        lastName
        displayUsername
        visitorCanEdit
        hasActivePortfolioSubscription
      }
    `,
    userProfileRef,
  );

  const {
    displayUsername,
    firstName,
    hasActivePortfolioSubscription,
    lastName,
    visitorCanEdit,
  } = data;

  const showProBadge = hasActivePortfolioSubscription;

  const {
    formState: { errors },
    handleSubmit,
    register,
    watch,
  } = useForm({
    defaultValues: {
      firstName: firstName ?? '',
      lastName: lastName ?? '',
    },
    mode: 'onChange',
    schema: z.object({
      firstName: z
        .string({
          invalid_type_error: 'You must provide your full name.',
          required_error: 'You must provide your full name.',
        })
        .trim()
        .nonempty('You must provide your full name.'),
      lastName: z
        .string({
          invalid_type_error: 'You must provide your full name.',
          required_error: 'You must provide your full name.',
        })
        .trim()
        .nonempty('You must provide your full name.'),
    }),
  });

  const { track } = useAnalytics();

  const { track: trackInput } = useTrackInput({
    first_name: watch('firstName'),
    last_name: watch('lastName'),
  });

  const { updateFullName } = useEditSidebar();

  const onEditFullName = useCallback(
    ({
      firstName: updatedFirstName,
      lastName: updatedLastName,
    }: {
      firstName: string;
      lastName: string;
    }) => {
      trackInput();
      updateFullName(
        { firstName: updatedFirstName, lastName: updatedLastName },
        () => {
          setIsEditing(false);
          toast.success('Name updated');
        },
      );
    },
    [toast, trackInput, updateFullName],
  );

  if (!firstName || !lastName || !displayUsername) {
    captureException('Received a profile with no name', {
      extra: {
        profileData: data,
      },
    });
    return null;
  }

  return visitorCanEdit && !disableEditing ? (
    <>
      {isEditing ? (
        <form onSubmit={handleSubmit(onEditFullName)}>
          <InputGroup>
            <FormField hasError={errors?.firstName !== undefined}>
              <TextField>
                <TextField.Input {...register('firstName')} />
              </TextField>
            </FormField>
            <FormField hasError={errors?.lastName !== undefined}>
              <TextField>
                <TextField.Input {...register('lastName')} />
              </TextField>
            </FormField>
          </InputGroup>

          <ValidationRow textAlign="center">
            <ValidationText
              hasError={
                errors?.firstName !== undefined ||
                errors?.lastName !== undefined
              }
            >
              {errors?.firstName === undefined ? (
                <ErrorMessage
                  errors={errors}
                  name="firstName"
                />
              ) : null}
              {errors?.lastName === undefined ? (
                <ErrorMessage
                  errors={errors}
                  name="lastName"
                />
              ) : null}
            </ValidationText>
          </ValidationRow>

          <EditableActions>
            <Button
              name="cancel"
              onClick={() => {
                track('button', {
                  action: 'tapped',
                  button_name: 'identity_inline_edit_full_name',
                });
                setIsEditing(false);
              }}
              size="xsmall"
              type="button"
              variant="V2TertiaryOutline"
            >
              Cancel
            </Button>
            <Button
              name="save"
              size="xsmall"
              type="submit"
              variant="V2Primary"
            >
              Save
            </Button>
          </EditableActions>
        </form>
      ) : (
        <InlineEditContainer
          label="Edit your full name"
          onClick={() => {
            if (!isEditing) setIsEditing(true);
          }}
          visitorCanEdit={visitorCanEdit}
        >
          <ReadonlyFullName
            displayUsername={displayUsername}
            firstName={firstName}
            hideLink
            lastName={lastName}
            showProBadge={showProBadge}
          />
          <EditIcon size={16} />
        </InlineEditContainer>
      )}
    </>
  ) : (
    <ReadonlyFullName
      displayUsername={displayUsername}
      firstName={firstName}
      lastName={lastName}
      openLinkInNewWindow={openLinkInNewWindow}
      showProBadge={showProBadge}
    />
  );
};
